import { Dispatch, SetStateAction } from 'react';
import {
  fetchVideo,
  getVideoUploadUrl,
  uploadVideo,
} from 'src/models/video/video';

import toast from './toast';

interface FileHandlerProps {
  selectedFiles: File[];
  setUploadingState: Dispatch<SetStateAction<boolean>>;
  setProgressState: Dispatch<SetStateAction<number>>;
  setAssetState: Dispatch<SetStateAction<any>>;
  handleUpload?: (file: File) => Promise<any>;
  setUploadedVideoId?: Dispatch<SetStateAction<any>>;
  handleVideoUploadFunc?: (
    file: File,
    setUploadProgress: Dispatch<SetStateAction<number>>
  ) => Promise<{ videoId: string; id: string }>;
  showFakeProgress?: boolean;
  skipVideoCheck?: boolean;
}

export const handleFileSelect = async ({
  selectedFiles,
  setUploadingState,
  setProgressState,
  setAssetState,
  handleUpload,
  setUploadedVideoId,
  handleVideoUploadFunc = handleVideoUpload,
  showFakeProgress = true,
  skipVideoCheck = false,
}: FileHandlerProps) => {
  if (selectedFiles.length === 0) {
    setAssetState(null);
    return;
  }

  const file = Array.isArray(selectedFiles) ? selectedFiles[0] : selectedFiles;

  setUploadingState(true);
  setProgressState(0);

  try {
    if (!skipVideoCheck && file.type.startsWith('video/')) {
      const { videoId } = await handleVideoUploadFunc(file, setProgressState);
      setUploadedVideoId?.(videoId);
    } else {
      if (showFakeProgress) {
        for (let i = 0; i < 100; i += 10) {
          setProgressState(i);
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }
      if (handleUpload) {
        const asset = await handleUpload(file);
        setAssetState(asset);
      }
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error occurred';
    toast.error(`Error uploading files: ${errorMessage}`);
  } finally {
    setUploadingState(false);
    setProgressState(100);
  }
};

// Add more file-related functions here as needed

export const handleVideoUpload = async (
  file: File,
  setUploadProgress?: Dispatch<SetStateAction<number>>
) => {
  const muxUploadUrl = await getVideoUploadUrl();
  const { new_asset_settings, url, id } = muxUploadUrl.data;
  const videoId = new_asset_settings.passthrough;
  await uploadVideo({
    url: url,
    file,
    progressCallback: (progress) => {
      if (setUploadProgress) {
        setUploadProgress(progress);
      }
    },
    errorCallback: () => toast.error('Failed to upload video'),
  });

  return { videoId, id };
};

interface VideoUploadState {
  isUploading: boolean;
  isProcessing: boolean;
  uploadProgress: number;
  videoId?: string;
  streamUrl?: string;
}

interface VideoUploadCallbacks {
  onUploadProgress?: (progress: number) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  onUploadComplete?: (videoId: string) => void;
  onProcessingComplete?: (streamUrl: string) => void;
  onError?: (error: Error) => void;
}

let latestUploadId = 0;

export const handleVideoUploadWithProcessing = async (
  file: File,
  callbacks: VideoUploadCallbacks = {},
  videoTypeParams?: {
    videoType?: string;
    videoTypeId?: string | number;
    videoId?: string;
  }
): Promise<VideoUploadState> => {
  const state: VideoUploadState = {
    isUploading: true,
    isProcessing: false,
    uploadProgress: 0,
  };

  const currentUploadId = ++latestUploadId;

  try {
    // Start upload
    const muxUploadUrl = await getVideoUploadUrl(videoTypeParams);
    const { new_asset_settings, url } = muxUploadUrl.data;
    const videoId = new_asset_settings.passthrough;
    state.videoId = videoId;

    // Handle upload with progress
    await uploadVideo({
      url,
      file,
      progressCallback: (progress) => {
        if (currentUploadId === latestUploadId) {
          state.uploadProgress = progress;
          callbacks.onUploadProgress?.(progress);
        }
      },
      errorCallback: (error) => {
        callbacks.onError && callbacks.onError(error);
        throw new Error(error || 'Failed to upload video');
      },
    });

    if (!file) return state;

    if (currentUploadId === latestUploadId) {
      // Upload complete, start processing
      state.isUploading = false;
      state.isProcessing = true;
      callbacks.onProcessingChange?.(true);
      callbacks.onUploadComplete?.(videoId);

      // Poll for processing completion
      while (state.isProcessing) {
        if (currentUploadId !== latestUploadId) break;
        const video = await fetchVideo({ id: videoId });
        if (video.embedUrl) {
          state.streamUrl = video.embedUrl;
          state.isProcessing = false;
          callbacks.onProcessingChange?.(false);
          callbacks.onProcessingComplete?.(video.embedUrl);
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    return state;
  } catch (error) {
    const errorObj =
      error instanceof Error ? error : new Error('Unknown error occurred');
    throw errorObj;
  }
};
